import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, RefreshCw, XCircle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

/**
 * Post-registration landing — instructs the user to confirm their email
 * before the account is fully activated. Keeps polling (via refresh) so the
 * UI advances automatically once the link is clicked in another tab.
 */
export default function CheckEmail() {
  const [params] = useSearchParams();
  const { role, user, commerce, logout, refresh } = useAuth();
  const subject = role === "client" ? user : role === "commerce" ? commerce : null;
  const email = subject?.email || params.get("email") || "";
  const intentRole = role || params.get("role") || "client";
  const nav = useNavigate();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Auto-redirect when already verified (handles the case where the user
  // verifies in another tab while staying on this page).
  useEffect(() => {
    if (subject?.emailVerified === true) {
      nav(role === "commerce" ? "/merchant/dashboard" : "/my-ticket", { replace: true });
    }
  }, [subject?.emailVerified, role, nav]);

  // Light polling — every 5s, refresh the auth context to detect verification.
  useEffect(() => {
    if (!subject || subject.emailVerified) return;
    const t = setInterval(() => { refresh(); }, 5000);
    return () => clearInterval(t);
  }, [subject, refresh]);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email de vérification renvoyé. Pensez à vérifier vos spams.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'envoyer l'email");
    } finally {
      setSending(false);
    }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      await refresh();
      // If still not verified after refresh, nudge the user.
      setTimeout(() => {
        const fresh = role === "client" ? user : commerce;
        if (!fresh?.emailVerified) {
          toast.message("Pas encore confirmé. Cliquez sur le lien dans l'email reçu.");
        }
      }, 400);
    } finally {
      setChecking(false);
    }
  };

  const cancelRegistration = async () => {
    setCancelling(true);
    try {
      await api.post("/auth/cancel-registration");
      toast.success("Création annulée — l'adresse email est de nouveau disponible.", { duration: 6000 });
      logout();
      nav("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Impossible d'annuler la création.";
      toast.error(detail);
      setConfirmCancel(false);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 md:py-16 max-w-lg" data-testid="check-email-page">
      <div className="border-2 border-ink rounded-xl bg-white p-5 sm:p-8 md:p-10 text-center shadow-md" data-testid="check-email-card">
        <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-brand/10 text-brand inline-flex items-center justify-center mb-4 sm:mb-6">
          <Mail size={26} className="sm:!w-8 sm:!h-8" />
        </div>
        <p className="overline text-[10px] sm:text-[10px] mb-1.5 sm:mb-2">Confirmation requise</p>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-ink leading-tight">
          Vérifiez votre adresse email
        </h1>
        <p className="mt-3 sm:mt-4 text-sm sm:text-base text-subtle leading-snug sm:leading-relaxed">
          Email envoyé à{email ? <> <strong className="text-ink break-all" data-testid="check-email-target">{email}</strong></> : null}.<br className="hidden sm:inline" />
          <span className="hidden sm:inline">Merci de cliquer sur le lien reçu pour activer votre compte.</span>
          <span className="sm:hidden"> Cliquez sur le lien reçu pour activer votre compte.</span>
        </p>
        <p className="mt-2 sm:mt-3 text-xs sm:text-xs text-subtle leading-snug">
          Lien valable 30 min · vérifiez aussi vos <em>Spam / Indésirables</em>.
        </p>

        {role === "commerce" && email ? (
          <div
            className="mt-4 sm:mt-6 rounded-lg border-2 border-brand/30 bg-brand/5 px-3.5 py-2.5 sm:px-4 sm:py-3 text-left"
            data-testid="check-email-identifier-block"
          >
            <p className="overline text-[10px] sm:text-[10px] text-brand">Votre identifiant de connexion est votre adresse email</p>
            <p
              className="mt-1 font-mono text-base sm:text-base font-black tracking-wider text-brand break-all"
              data-testid="check-email-identifier-value"
            >
              {email}
            </p>
            <p className="mt-1 text-[11px] sm:text-[11px] text-subtle leading-snug">
              À saisir dans le champ « Adresse email » lors de la connexion.
            </p>
          </div>
        ) : null}

        <div className="mt-5 sm:mt-8 flex flex-col gap-2.5 sm:gap-3">
          {subject ? (
            <Button
              onClick={checkNow}
              disabled={checking}
              className="h-11 sm:h-12 bg-brand hover:bg-brand-hover text-white rounded-md font-bold text-sm sm:text-base"
              data-testid="check-email-refresh-btn"
            >
              <CheckCircle2 size={15} className="mr-2" />
              {checking ? "Vérification…" : "J'ai vérifié, continuer"}
            </Button>
          ) : null}
          {subject ? (
            <Button
              variant="outline"
              onClick={resend}
              disabled={sending}
              className="h-11 sm:h-12 border border-ink/30 text-ink hover:bg-ink hover:text-white rounded-md font-bold text-sm sm:text-base"
              data-testid="check-email-resend-btn"
            >
              <RefreshCw size={15} className="mr-2" />
              {sending ? "Envoi…" : "Renvoyer l'email de vérification"}
            </Button>
          ) : (
            <Button asChild variant="outline" className="h-11 sm:h-12 border border-ink/30 text-ink rounded-md font-bold text-sm sm:text-base">
              <Link to={intentRole === "commerce" ? "/commerce/login" : "/visitor/login"}>
                Se connecter
              </Link>
            </Button>
          )}
          {subject ? (
            confirmCancel ? (
              <div
                className="rounded-md border-2 border-destructive bg-destructive/5 p-3 sm:p-4 text-left"
                data-testid="check-email-cancel-confirm-block"
              >
                <p className="text-sm font-bold text-destructive">
                  Confirmer l'annulation ?
                </p>
                <p className="mt-1 text-xs text-subtle leading-snug">
                  La création sera abandonnée et <strong className="text-ink">l'email saisi redevient immédiatement disponible</strong>.
                </p>
                <p className="mt-1 text-[11px] text-subtle leading-snug">
                  Sans action, le compte est supprimé après 30 min sans vérification.
                </p>
                <div className="mt-2.5 sm:mt-3 flex gap-2">
                  <Button
                    onClick={cancelRegistration}
                    disabled={cancelling}
                    className="h-10 bg-destructive hover:bg-destructive/90 text-white rounded-md font-bold flex-1 text-sm"
                    data-testid="check-email-cancel-confirm-btn"
                  >
                    {cancelling ? "Annulation…" : "Oui, annuler"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmCancel(false)}
                    disabled={cancelling}
                    className="h-10 border border-border rounded-md font-bold text-sm"
                    data-testid="check-email-cancel-keep-btn"
                  >
                    Non, conserver
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setConfirmCancel(true)}
                className="h-10 text-sm text-subtle hover:text-destructive"
                data-testid="check-email-cancel-btn"
              >
                <XCircle size={14} className="mr-2" /> Annuler la création
              </Button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
